import { DbAuthenticationMethod } from 'Components/Admin/Core/Sections/type';
import { actions } from 'Data/ReduxViewModels/ServiceSetup';
import deepEquals from 'Lib/Object/deepEquals';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ButtonGroup,
  Dialog,
  Field,
  InlineSpinner,
  Message,
  PasswordInput,
  RadioGroup,
  TextAreaInput,
  TextInput,
  TextOutput,
} from '../../Core';
import { FieldSet, ListFieldSection, PageSection } from '../../Layout';

const Connection: React.FC = () => {
  const dispatch = useDispatch();

  const restarting = useSelector((state) => state.serviceSetup.qestnetRestart.restarting);
  const connection = useSelector((state) => state.serviceSetup.connection, deepEquals);
  const success = useSelector((state) => state.serviceSetup.databaseConnectionTest.success);
  const testing = useSelector((state) => state.serviceSetup.databaseConnectionTest.testing);
  const statusMessage = useSelector((state) => state.serviceSetup.status.message);
  const syncing = useSelector(
    (state) => state.serviceSetup.status.synchronising || state.serviceSetup.status.retrievingData
  );
  const disabled = testing || restarting;

  const onChange = (name: string, data: any) => {
    dispatch(actions.setValue({ name, value: data.value, sync: data.submitValues }));
  };

  return !connection ? null : (
    <PageSection>
      <Dialog visible={true} width={500} title="QEST Platform Database Connection Setup">
        <Message>
          To begin using the QEST Platform a connection needs to be configured to the QEST Platform database.
        </Message>
        <FieldSet>
          <FieldSet key={connection.name}>
            <Field label="Name" input={<TextOutput />} value={connection.name} disabled={disabled} />
            <Field
              input={<RadioGroup />}
              label="Configuration"
              value={connection.isAssisted}
              onChange={(e: any) => onChange('isAssisted', e.data)}
              disabled={disabled}
              options={[
                { label: 'Assisted', value: true },
                { label: 'Manual', value: false },
              ]}
            />
            {connection.isAssisted ? (
              <>
                <Field
                  input={<RadioGroup />}
                  label="Authentication Method"
                  onChange={(e: any) => onChange('authenticationMethod', e.data)}
                  value={connection.authenticationMethod}
                  defaultValue={DbAuthenticationMethod.SQLServerAuth}
                  disabled={disabled}
                  options={[
                    { label: 'Windows Authentication', value: DbAuthenticationMethod.WindowAuth },
                    { label: 'Entra ID Integrated Authentication', value: DbAuthenticationMethod.EntraIDAuth },
                    { label: 'SQL Server Authentication', value: DbAuthenticationMethod.SQLServerAuth },
                  ]}
                />
                <Field
                  label="Server"
                  input={<TextInput />}
                  disabled={disabled}
                  value={connection.dataSource}
                  onChange={(e: any) => onChange('dataSource', e.data)}
                />
                <Field
                  label="Database Name"
                  input={<TextInput />}
                  disabled={disabled}
                  value={connection.initialCatalog}
                  onChange={(e: any) => onChange('initialCatalog', e.data)}
                  messages={
                    connection.authenticationMethod !== DbAuthenticationMethod.SQLServerAuth
                      ? [{ message: statusMessage?.message, severity: statusMessage?.severity }]
                      : undefined
                  }
                />
              </>
            ) : (
              <>
                <Field
                  input={<TextAreaInput />}
                  label="Connection String"
                  disabled={disabled}
                  value={connection.manualConnectionString}
                  onChange={(e: any) => onChange('manualConnectionString', e.data)}
                  rows={4}
                  messages={
                    connection.integratedSecurity
                      ? [{ message: statusMessage?.message, severity: statusMessage?.severity }]
                      : undefined
                  }
                />
              </>
            )}
            {connection.isAssisted && connection.authenticationMethod === DbAuthenticationMethod.SQLServerAuth ? (
              <>
                <Field
                  label="User ID"
                  input={<TextInput />}
                  value={connection.userID}
                  onChange={(e: any) => onChange('userId', e.data)}
                  disabled={disabled}
                />
                <Field
                  label="Password"
                  input={<PasswordInput />}
                  disabled={disabled}
                  onChange={(e: any) => onChange('password', e.data)}
                  value={connection.password}
                  messages={[{ message: statusMessage?.message, severity: statusMessage?.severity }]}
                />
              </>
            ) : null}
          </FieldSet>
          <ListFieldSection>
            <ButtonGroup>
              <Button
                loading={testing}
                onClick={() => dispatch(actions.testDatabaseConnection())}
                primary
                disabled={
                  (connection.isAssisted && (connection.dataSource === '' || connection.initialCatalog === '')) ||
                  (!connection.isAssisted && connection.manualConnectionString === '') ||
                  syncing ||
                  disabled
                }
              >
                Test Database Connection
              </Button>
              {syncing && (
                <div style={{ marginTop: 8 }}>
                  <InlineSpinner />
                </div>
              )}
            </ButtonGroup>
            <ButtonGroup align={'right'}>
              <Button
                loading={restarting}
                onClick={() => dispatch(actions.restartQestnet())}
                primary
                disabled={!success}
              >
                Start the QEST Platform
              </Button>
            </ButtonGroup>
          </ListFieldSection>
        </FieldSet>
      </Dialog>
    </PageSection>
  );
};

export default Connection;
export { Connection };
